import "../Overview/EndpointOverview.css";
import "../Overview/EndpointDataSections.css";
import "../CloudAnalytics/CloudEntityAnalytics.css";
import "./GlobalAgenticActivity.css";
import { useState, useMemo, useEffect, useCallback, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Bot,
  Monitor,
  Users,
  Activity,
  AlertTriangle,
  CheckCircle2,
  ShieldOff,
  Search,
  Zap,
  LayoutGrid,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  HardDrive,
  Network,
  Terminal,
  ClipboardList,
  Globe2,
  Loader2,
  RefreshCw,
  CalendarDays,
  type LucideIcon,
} from "lucide-react";
import AgenticActivity from "./AgenticActivity";
import { formatAgentDisplayName } from "./agentDisplayName";
import useUserStore from "../../../services/userStore";
import {
  getAgenticActivityOverview,
  getAgenticActivityTimeline,
  getAgentInventory,
  getAgentEndpoints,
  getTopEndpoints,
  getTopUsers,
  updateAgentStatus,
  buildDashboardRangeParams,
  AgentInventoryItem,
  AgentEndpointDetail,
  AgenticActivityOverview,
  AgenticActivityTimelinePoint,
  TopEndpointItem,
  TopUserItem,
  AgentGovernanceStatus,
  DashboardDuration,
} from "./Api/globalAgenticActivityApi";
import { myLocalStorage } from "../../StorageHelper";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import { cn } from "../../../utils/utils";

type AgentCap = "fs" | "net" | "shell" | "clipboard" | "browser";
type GovernanceFilter = "all" | "APPROVED" | "BLOCKED";

const CAPABILITY_MAP: Record<string, AgentCap> = {
  FILE_SYSTEM: "fs",
  NETWORK: "net",
  SHELL: "shell",
  CLIPBOARD: "clipboard",
  BROWSER: "browser",
};

const PALETTE = [
  { badgeCls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border border-orange-200 dark:border-orange-700", cardRing: "border-orange-200 dark:border-orange-800", iconBg: "bg-orange-100 dark:bg-orange-900/50", iconText: "text-orange-600 dark:text-orange-400", dotCls: "bg-orange-500" },
  { badgeCls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border border-sky-200 dark:border-sky-700", cardRing: "border-sky-200 dark:border-sky-800", iconBg: "bg-sky-100 dark:bg-sky-900/50", iconText: "text-sky-600 dark:text-sky-400", dotCls: "bg-sky-500" },
  { badgeCls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-violet-200 dark:border-violet-700", cardRing: "border-violet-200 dark:border-violet-800", iconBg: "bg-violet-100 dark:bg-violet-900/50", iconText: "text-violet-600 dark:text-violet-400", dotCls: "bg-violet-500" },
  { badgeCls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border border-teal-200 dark:border-teal-700", cardRing: "border-teal-200 dark:border-teal-800", iconBg: "bg-teal-100 dark:bg-teal-900/50", iconText: "text-teal-600 dark:text-teal-400", dotCls: "bg-teal-500" },
  { badgeCls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700", cardRing: "border-blue-200 dark:border-blue-800", iconBg: "bg-blue-100 dark:bg-blue-900/50", iconText: "text-blue-600 dark:text-blue-400", dotCls: "bg-blue-500" },
  { badgeCls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700", cardRing: "border-emerald-200 dark:border-emerald-800", iconBg: "bg-emerald-100 dark:bg-emerald-900/50", iconText: "text-emerald-600 dark:text-emerald-400", dotCls: "bg-emerald-500" },
  { badgeCls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-700", cardRing: "border-rose-200 dark:border-rose-800", iconBg: "bg-rose-100 dark:bg-rose-900/50", iconText: "text-rose-600 dark:text-rose-400", dotCls: "bg-rose-500" },
];

function hashAgentKey(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % PALETTE.length;
  }
  return hash;
}

function getAgentStyle(agentKey: string) {
  return PALETTE[hashAgentKey(agentKey)] ?? PALETTE[0];
}

function mapCapabilities(caps: string[]): AgentCap[] {
  return caps
    .map(c => CAPABILITY_MAP[c.toUpperCase()])
    .filter(Boolean) as AgentCap[];
}

function relTime(ts: string | null | undefined): string {
  if (!ts) return "—";
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return "—";
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "< 1h ago";
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatChartDate(date: string): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function AgentPill({ agentKey, displayName }: { agentKey: string; displayName?: string }) {
  const style = getAgentStyle(agentKey);
  return (
    <span className={`agentic-dash-pill inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${style.badgeCls}`}>
      <Bot className="w-3 h-3" />{displayName ?? formatAgentDisplayName(agentKey)}
    </span>
  );
}

function StatusBadge({ status }: { status: AgentGovernanceStatus }) {
  const cfg = status === "APPROVED"
    ? { label: "Approved", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700", Icon: CheckCircle2 }
    : status === "BLOCKED"
      ? { label: "Blocked", cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-700", Icon: ShieldOff }
      : { label: "Pending", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-700", Icon: AlertTriangle };
  return (
    <span className={`agentic-dash-pill inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

const CAP_CONFIG: Record<AgentCap, {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  cls: string;
}> = {
  fs: { label: "File System", Icon: HardDrive, cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800" },
  net: { label: "Network", Icon: Network, cls: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800" },
  shell: { label: "Shell", Icon: Terminal, cls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800" },
  clipboard: { label: "Clipboard", Icon: ClipboardList, cls: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700" },
  browser: { label: "Browser", Icon: Globe2, cls: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800" },
};


function CapabilityTags({ caps, size = "md" }: { caps: AgentCap[]; size?: "sm" | "md" }) {
  if (!caps.length) {
    return <span className="agentic-dash-muted-note">No capabilities detected yet</span>;
  }
  return (
    <div className="agentic-dash-caps" data-size={size}>
      {caps.map(c => {
        const { label, Icon, cls } = CAP_CONFIG[c];
        return (
          <span key={c} title={label} className={`agentic-dash-cap inline-flex items-center gap-1 rounded border font-medium ${cls}`}>
            <Icon />{label}
          </span>
        );
      })}
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="overview-empty-state" role="status">
      <span className="overview-empty-icon" aria-hidden="true"><Loader2 className="animate-spin" /></span>
      <p>{label}</p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="overview-empty-state agentic-dash-error-state" role="alert">
      <span className="overview-empty-icon" aria-hidden="true"><AlertTriangle /></span>
      <p>{message}</p>
      <Button size="sm" variant="outline" className="agentic-dash-button" onClick={onRetry}>
        <RefreshCw />Retry
      </Button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="overview-empty-state">
      <span className="overview-empty-icon" aria-hidden="true"><Bot /></span>
      <p>{message}</p>
    </div>
  );
}

type KpiTone = "violet" | "orange" | "sky" | "teal";

function StatCard({ icon: Icon, label, value, sub, tone }: {
  icon: LucideIcon;
  label: string;
  value: number;
  sub?: string;
  tone: KpiTone;
}) {
  return (
    <Card className="overview-card agentic-kpi-card" data-tone={tone}>
      <div className="agentic-kpi-copy">
        <span className="agentic-kpi-label">{label}</span>
        <span className="agentic-kpi-value">{value.toLocaleString()}</span>
        {sub && <span className="agentic-kpi-sub">{sub}</span>}
      </div>
      <span className="agentic-kpi-icon" aria-hidden="true"><Icon /></span>
    </Card>
  );
}

function durationLabel(duration: DashboardDuration, customStart?: string, customEnd?: string): string {
  if (duration === "custom" && customStart && customEnd) {
    return `${customStart} – ${customEnd}`;
  }
  if (duration === "7") return "Last 7 Days";
  if (duration === "30") return "Last 30 Days";
  if (duration === "90") return "Last 90 Days";
  return "Custom range";
}

function ActivityTimeline({ data, loading, label }: { data: AgenticActivityTimelinePoint[]; loading: boolean; label: string }) {
  const chartData = useMemo(
    () => data.map(point => ({ t: formatChartDate(point.date), events: point.events })),
    [data]
  );
  const totalEvents = useMemo(() => data.reduce((sum, point) => sum + point.events, 0), [data]);
  const gradientId = `agentic-dash-${useId().replace(/:/g, "")}`;

  return (
    <Card className="overview-card overview-timeline-card agentic-dash-timeline">
      <OverviewCardHeader
        fromdetails
        title={`Agent Activity — ${label}`}
        description="Total events across all endpoints"
        icon={Activity}
        detail={!loading && chartData.length > 0 ? `${totalEvents.toLocaleString()} events` : undefined}
      />
      <CardContent className="overview-card-content">
        {loading ? (
          <div className="agentic-dash-chart agentic-dash-chart-state"><Loader2 className="animate-spin" /></div>
        ) : chartData.length === 0 ? (
          <EmptyState message="No activity recorded in the selected period" />
        ) : (
          <div className="agentic-dash-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="4 6" stroke="hsl(var(--border))" className="opacity-60" />
                <XAxis dataKey="t" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} tickMargin={12} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip wrapperClassName="overview-chart-tooltip" formatter={(v: number) => [v, "events"]} />
                <Area
                  type="monotone"
                  dataKey="events"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={{ r: 4, fill: "hsl(var(--primary))", stroke: "hsl(var(--card))", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AgentCard({ agent, maxEvents, onStatusChange, onClick, isSelected, statusUpdating }: {
  agent: AgentInventoryItem;
  maxEvents: number;
  onStatusChange: (agentId: number, status: AgentGovernanceStatus) => void;
  onClick: () => void;
  isSelected: boolean;
  statusUpdating: boolean;
}) {
  const style = getAgentStyle(agent.agentKey);
  const caps = mapCapabilities(agent.capabilities);
  const share = maxEvents > 0 ? Math.round((agent.events / maxEvents) * 100) : 0;

  return (
    <div onClick={onClick} className="agentic-agent-card" data-selected={isSelected}>
      <div className="agentic-agent-head">
        <span className={`agentic-agent-icon ${style.iconBg}`} aria-hidden="true">
          <Bot className={style.iconText} />
        </span>
        <div className="min-w-0">
          <p className="agentic-agent-name">{formatAgentDisplayName(agent.agentKey)}</p>
          <p className="agentic-agent-sub">First seen {relTime(agent.firstSeen)}</p>
        </div>
        {/**
         * <StatusBadge status={agent.status} />
         */}
      </div>

      <div className="agentic-agent-metrics">
        <div><strong>{agent.events.toLocaleString()}</strong><span>events</span></div>
        <div><strong>{agent.endpoints.toLocaleString()}</strong><span>endpoints</span></div>
        <div><strong>{agent.users.toLocaleString()}</strong><span>users</span></div>
      </div>

      <div className="agentic-agent-section">
        <p className="agentic-agent-label">System Access</p>
        <CapabilityTags caps={caps} />
      </div>

      <div className="agentic-agent-section">
        <div className="agentic-agent-share">
          <span>Activity share</span>
          <span>{agent.activityShare || share}%</span>
        </div>
        <div className="agentic-agent-track">
          <div className={style.dotCls} style={{ width: `${agent.activityShare || share}%` }} />
        </div>
      </div>

      <div className="agentic-agent-footer">
        <span>
          <Clock aria-hidden="true" />Last seen {relTime(agent.lastSeen)}
        </span>
        {/**
         * <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          {agent.status !== "APPROVED" && (
            <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "APPROVED")}>
              <CheckCircle2 className="w-3 h-3 mr-1" />Approve
            </Button>
          )}
          {agent.status !== "BLOCKED" && (
            <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "BLOCKED")}>
              <ShieldOff className="w-3 h-3 mr-1" />Block
            </Button>
          )}
        </div>
         */}
      </div>
    </div>
  );
}

const AGENTS_PER_PAGE = 9;
const ENDPOINT_PAGE_SIZE = 100;

function AgentDrillDown({ agent, tenantId, onStatusChange, onClose, statusUpdating }: {
  agent: AgentInventoryItem;
  tenantId: string;
  onStatusChange: (agentId: number, status: AgentGovernanceStatus) => void;
  onClose: () => void;
  statusUpdating: boolean;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [endpoints, setEndpoints] = useState<AgentEndpointDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  const loadEndpoints = useCallback(async (append: boolean, startOffset: number, query: string) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await getAgentEndpoints(tenantId, agent.agentId, {
        offset: startOffset,
        limit: ENDPOINT_PAGE_SIZE,
        search: query || undefined,
      });
      const items = response?.items ?? [];
      setEndpoints(prev => append ? [...prev, ...items] : items);
      setTotalCount(response?.total ?? items.length);
      setHasMore(response?.hasMore ?? false);
    } catch {
      setError("Failed to load endpoints.");
      if (!append) setEndpoints([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [tenantId, agent.agentId]);

  useEffect(() => {
    loadEndpoints(false, 0, searchQuery);
  }, [loadEndpoints, searchQuery]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (value.trim() === "" && searchQuery.trim() !== "") {
      setSearchQuery("");
    }
  };

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore || loadingRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      loadEndpoints(true, endpoints.length, searchQuery);
    }
  }, [endpoints.length, hasMore, loadingMore, loadEndpoints, searchQuery]);

  const style = getAgentStyle(agent.agentKey);
  const caps = mapCapabilities(agent.capabilities);


  return (
    <div className="overview-card agentic-drilldown">
      <div className="agentic-drilldown-head">
        <div className="agentic-drilldown-title">
          <span className={`agentic-agent-icon ${style.iconBg}`} aria-hidden="true">
            <Bot className={style.iconText} />
          </span>
          <div className="min-w-0">
            <p className="agentic-agent-name">{formatAgentDisplayName(agent.agentKey)}</p>
            <div className="mt-1"><StatusBadge status={agent.status} /></div>
          </div>
          <Button size="icon" variant="ghost" className="agentic-drilldown-close" onClick={onClose} aria-label="Close agent details">
            <X />
          </Button>
        </div>
        <CapabilityTags caps={caps} size="sm" />
        <div className="agentic-agent-metrics">
          {[
            { label: "Events", value: agent.events },
            { label: "Endpoints", value: agent.endpoints },
            { label: "Users", value: agent.users },
          ].map(s => (
            <div key={s.label}><strong>{s.value.toLocaleString()}</strong><span>{s.label}</span></div>
          ))}
        </div>
        <div className="agentic-drilldown-actions">
          {agent.status !== "APPROVED" && (
            <Button size="sm" variant="outline" className="agentic-dash-button agentic-approve-action" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "APPROVED")}>
              <CheckCircle2 />Approve
            </Button>
          )}
          {agent.status !== "BLOCKED" && (
            <Button size="sm" variant="outline" className="agentic-dash-button agentic-block-action" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "BLOCKED")}>
              <ShieldOff />Block Agent
            </Button>
          )}
        </div>
      </div>

      <div className="agentic-drilldown-body">
        <div className="agentic-drilldown-search">
          <div className="agentic-drilldown-search-head">
            <p>Endpoints using this agent</p>
            <span className="overview-card-detail">{totalCount.toLocaleString()} found</span>
          </div>
          <div className="agentic-dash-search">
            <span className="agentic-dash-search-field">
              <Search aria-hidden="true" />
              <Input
                placeholder="Search by name, OS, user…"
                value={searchInput}
                onChange={e => handleSearchInputChange(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
                aria-label="Search endpoints using this agent"
              />
            </span>
            <Button size="icon" variant="outline" className="agentic-dash-icon-button" onClick={handleSearch} aria-label="Search endpoints">
              <Search />
            </Button>
          </div>
        </div>

        <div ref={scrollRef} className="agentic-drilldown-list" onScroll={handleScroll}>
          {loading ? <LoadingState label="Loading endpoints…" /> :
            error ? <ErrorState message={error} onRetry={() => loadEndpoints(false, 0, searchQuery)} /> :
              endpoints.length === 0 ? <EmptyState message="No endpoints match your search" /> :
                <>
                  {endpoints.map(ep => (
                    <div key={`${ep.computerId}-${ep.lastSeen ?? ""}`} className="agentic-rank-row">
                      <span className="agentic-rank-avatar" aria-hidden="true"><Monitor /></span>
                      <div className="agentic-rank-copy">
                        <p className="agentic-rank-name">{ep.computerName}</p>
                        {ep.osName && <p className="agentic-rank-sub">{ep.osName}</p>}
                        {ep.otherAgents.length > 0 && (
                          <div className="agentic-rank-pills">
                            {ep.otherAgents.map(a => <AgentPill key={a} agentKey={a} />)}
                          </div>
                        )}
                        {ep.users.length > 0 && (
                          <div className="agentic-rank-meta">
                            {ep.users.map(u => (
                              <span key={u}><User aria-hidden="true" />{u}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="agentic-rank-value">
                        <strong>{ep.events.toLocaleString()}</strong>
                        <span>events</span>
                        <span>{relTime(ep.lastSeen)}</span>
                      </div>
                    </div>
                  ))}
                  {loadingMore && (
                    <div className="agentic-drilldown-more" role="status">
                      <Loader2 className="animate-spin" />
                      Loading more…
                    </div>
                  )}
                </>
          }
        </div>
      </div>
    </div>
  );
}

function EndpointsTable({ items, search, loading }: { items: TopEndpointItem[]; search: string; loading: boolean }) {
  const filtered = useMemo(
    () => items.filter(e => e.computerName.toLowerCase().includes(search.toLowerCase())),
    [items, search]
  );

  return (
    <div className="agentic-rank-panel">
      <div className="agentic-rank-panel-head">
        <span className="agentic-rank-panel-icon" aria-hidden="true"><Monitor /></span>
        <p>Top Endpoints</p>
        {!loading && filtered.length > 0 && <span className="overview-card-detail">{Math.min(filtered.length, 10)} shown</span>}
      </div>
      {loading ? <LoadingState label="Loading endpoints…" /> :
        filtered.length === 0 ? <EmptyState message="No endpoint activity found" /> :
          <div className="agentic-rank-list">
            {filtered.slice(0, 10).map((ep, index) => (
              <div key={ep.computerId} className="agentic-rank-row">
                <span className="agentic-rank-position">{index + 1}</span>
                <div className="agentic-rank-copy">
                  <div className="agentic-rank-title">
                    <span className="agentic-rank-name">{ep.computerName}</span>
                    {ep.osName && <span className="agentic-rank-sub">{ep.osName}</span>}
                  </div>
                  <div className="agentic-rank-pills">
                    {ep.agents.map(a => <AgentPill key={a} agentKey={a} />)}
                  </div>
                </div>
                <div className="agentic-rank-value">
                  <strong>{ep.events.toLocaleString()}</strong>
                  <span>events</span>
                  <span>{relTime(ep.lastSeen)}</span>
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}

function UsersTable({ items, search, loading, blockedAgentKeys }: {
  items: TopUserItem[]; search: string; loading: boolean; blockedAgentKeys: Set<string>;
}) {
  const filtered = useMemo(
    () => items.filter(u =>
      u.username.toLowerCase().includes(search.toLowerCase())
    ),
    [items, search]
  );

  return (
    <div className="agentic-rank-panel">
      <div className="agentic-rank-panel-head">
        <span className="agentic-rank-panel-icon" aria-hidden="true"><Users /></span>
        <p>Top Users</p>
        {!loading && filtered.length > 0 && <span className="overview-card-detail">{Math.min(filtered.length, 10)} shown</span>}
      </div>
      {loading ? <LoadingState label="Loading users…" /> :
        filtered.length === 0 ? <EmptyState message="No user activity found" /> :
          <div className="agentic-rank-list">
            {filtered.slice(0, 10).map((u, index) => {
              const flagged = u.agents.some(a => blockedAgentKeys.has(a));
              return (
                <div key={u.userId} className="agentic-rank-row" data-flagged={flagged}>
                  <span className="agentic-rank-position">{index + 1}</span>
                  <div className="agentic-rank-copy">
                    <div className="agentic-rank-title">
                      <span className="agentic-rank-name">{u.username}</span>
                      {flagged && <AlertTriangle className="agentic-rank-flag" aria-label="Uses a blocked agent" />}
                    </div>
                    <div className="agentic-rank-pills">
                      {u.agents.map(a => <AgentPill key={a} agentKey={a} />)}
                    </div>
                    {u.endpoints.length > 0 && (
                      <div className="agentic-rank-meta">
                        {u.endpoints.map(ep => (
                          <span key={ep}><Monitor aria-hidden="true" />{ep}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="agentic-rank-value">
                    <strong>{u.events.toLocaleString()}</strong>
                    <span>events</span>
                  </div>
                </div>
              );
            })}
          </div>}
    </div>
  );
}

function OverviewTab({ tenantId, refreshKey, onRefresh, onOverviewChange, periodSlot, actionSlot, extraSlot }: {
  tenantId: string;
  refreshKey: number;
  onRefresh: () => void;
  onOverviewChange?: (overview: AgenticActivityOverview | null) => void;
  periodSlot: HTMLDivElement | null;
  actionSlot: HTMLDivElement | null;
  extraSlot: HTMLDivElement | null;
}) {
  const [tableSearchInput, setTableSearchInput] = useState("");
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [agentFilter] = useState<GovernanceFilter>("all");
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [agentPage, setAgentPage] = useState(1);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [duration, setDuration] = useState<DashboardDuration>("7");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [appliedCustomStart, setAppliedCustomStart] = useState("");
  const [appliedCustomEnd, setAppliedCustomEnd] = useState("");

  const [agentNameInput, setAgentNameInput] = useState("");
  const [agentSearchQuery, setAgentSearchQuery] = useState("");
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [overview, setOverview] = useState<AgenticActivityOverview | null>(null);
  const [timeline, setTimeline] = useState<AgenticActivityTimelinePoint[]>([]);
  const [agents, setAgents] = useState<AgentInventoryItem[]>([]);
  const [topEndpoints, setTopEndpoints] = useState<TopEndpointItem[]>([]);
  const [topUsers, setTopUsers] = useState<TopUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rangeParams = useMemo(() => {
    if (duration === "custom") {
      return buildDashboardRangeParams(duration, appliedCustomStart, appliedCustomEnd);
    }
    return buildDashboardRangeParams(duration);
  }, [duration, appliedCustomStart, appliedCustomEnd]);

  const timelineLabel = useMemo(
    () => durationLabel(duration, appliedCustomStart || customStartDate, appliedCustomEnd || customEndDate),
    [duration, appliedCustomStart, appliedCustomEnd, customStartDate, customEndDate]
  );

  const isPeriodFilterLoading = loading || agentsLoading;

  useEffect(() => {
    onOverviewChange?.(overview);
  }, [overview, onOverviewChange]);

  const loadAgents = useCallback(async () => {
    if (!tenantId) return;
    if (duration === "custom" && (!appliedCustomStart || !appliedCustomEnd)) return;

    setAgentsLoading(true);
    try {
      const agentsData = await getAgentInventory(tenantId, {
        ...rangeParams,
        status: agentFilter === "all" ? undefined : agentFilter,
        search: agentSearchQuery || undefined,
      });
      setAgents(agentsData ?? []);
    } catch {
      setError("Failed to load agent inventory.");
    } finally {
      setAgentsLoading(false);
    }
  }, [tenantId, rangeParams, agentFilter, agentSearchQuery, refreshKey, duration, appliedCustomStart, appliedCustomEnd]);

  const loadDashboard = useCallback(async () => {
    if (!tenantId) return;
    if (duration === "custom" && (!appliedCustomStart || !appliedCustomEnd)) return;

    setLoading(true);
    setError(null);
    try {
      const [overviewData, timelineData, endpointsData, usersData] = await Promise.all([
        getAgenticActivityOverview(tenantId, rangeParams),
        getAgenticActivityTimeline(tenantId, rangeParams),
        getTopEndpoints(tenantId, rangeParams),
        getTopUsers(tenantId, rangeParams),
      ]);
      setOverview(overviewData ?? null);
      setTimeline(timelineData ?? []);
      setTopEndpoints(endpointsData ?? []);
      setTopUsers(usersData ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load agentic activity dashboard.";
      setError(typeof message === "string" ? message : "Failed to load agentic activity dashboard.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, rangeParams, duration, appliedCustomStart, appliedCustomEnd, refreshKey]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleAgentSearch = () => {
    setAgentSearchQuery(agentNameInput.trim());
    setAgentPage(1);
    setSelectedAgentId(null);
  };

  const handleAgentSearchInputChange = (value: string) => {
    setAgentNameInput(value);
    if (value.trim() === "" && agentSearchQuery.trim() !== "") {
      setAgentSearchQuery("");
      setAgentPage(1);
      setSelectedAgentId(null);
    }
  };

  const handleTableSearch = () => {
    setTableSearchQuery(tableSearchInput.trim());
  };

  const handleTableSearchInputChange = (value: string) => {
    setTableSearchInput(value);
    if (value.trim() === "" && tableSearchQuery.trim() !== "") {
      setTableSearchQuery("");
    }
  };

  const handleDurationChange = (value: DashboardDuration) => {
    setDuration(value);
    setAgentPage(1);
    setSelectedAgentId(null);
    if (value !== "custom") {
      setAppliedCustomStart("");
      setAppliedCustomEnd("");
    }
  };

  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) return;
    setAppliedCustomStart(customStartDate);
    setAppliedCustomEnd(customEndDate);
    setAgentPage(1);
    setSelectedAgentId(null);
  };

  const handleStatusChange = async (agentId: number, status: AgentGovernanceStatus) => {
    setStatusUpdating(true);
    try {
      const updated = await updateAgentStatus(tenantId, agentId, status);
      if (updated) {
        setAgents(prev => prev.map(a => a.agentId === agentId ? updated : a));
        const overviewData = await getAgenticActivityOverview(tenantId, rangeParams);
        setOverview(overviewData ?? null);
      }
    } catch {
      setError("Failed to update agent status.");
    } finally {
      setStatusUpdating(false);
    }
  };

  const filteredAgents = agents;
  const maxEvents = useMemo(() => Math.max(0, ...filteredAgents.map(a => a.events)), [filteredAgents]);
  const totalAgentPages = Math.max(1, Math.ceil(filteredAgents.length / AGENTS_PER_PAGE));
  const pagedAgents = filteredAgents.slice((agentPage - 1) * AGENTS_PER_PAGE, agentPage * AGENTS_PER_PAGE);
  const selectedAgent = agents.find(a => a.agentId === selectedAgentId) ?? null;
  const blockedAgentKeys = useMemo(
    () => new Set(agents.filter(a => a.status === "BLOCKED").map(a => a.agentKey)),
    [agents]
  );

  if (!tenantId) {
    return <EmptyState message="Select a tenant to view agentic activity" />;
  }

  if (loading && !overview) {
    return <LoadingState label="Loading dashboard…" />;
  }

  if (error && !overview) {
    return <ErrorState message={error} onRetry={loadDashboard} />;
  }


  const periodControls = periodSlot && createPortal(
    <div className="endpoint-filter-field">
      <span className="endpoint-filter-label"><CalendarDays aria-hidden="true" />Dashboard period</span>
      <Select value={duration} onValueChange={v => handleDurationChange(v as DashboardDuration)} disabled={isPeriodFilterLoading}>
        <SelectTrigger className="agentic-dash-select w-44" aria-label="Dashboard period">
          <SelectValue placeholder="Duration" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7">Last 7 days</SelectItem>
          <SelectItem value="30">Last 30 days</SelectItem>
          <SelectItem value="90">Last 90 days</SelectItem>
          <SelectItem value="custom">Custom date</SelectItem>
        </SelectContent>
      </Select>
    </div>,
    periodSlot
  );

  const customRangeControls = extraSlot && duration === "custom" && createPortal(
    <>
      <div className="endpoint-filter-field">
        <span className="endpoint-filter-label"><CalendarDays aria-hidden="true" />From</span>
        <Input type="date" className="agentic-dash-date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} disabled={isPeriodFilterLoading} aria-label="Custom period start date" />
      </div>
      <div className="endpoint-filter-field">
        <span className="endpoint-filter-label"><CalendarDays aria-hidden="true" />To</span>
        <Input type="date" className="agentic-dash-date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} disabled={isPeriodFilterLoading} aria-label="Custom period end date" />
      </div>
      <Button size="sm" variant="outline" className="agentic-dash-button" onClick={handleApplyCustomRange} disabled={!customStartDate || !customEndDate || isPeriodFilterLoading}>
        Apply
      </Button>
    </>,
    extraSlot
  );

  const periodActions = actionSlot && createPortal(
    <>
      {isPeriodFilterLoading && (
        <span className="agentic-dash-status" role="status">
          <Loader2 className="animate-spin" aria-hidden="true" />
          Applying filters…
        </span>
      )}
      <Button size="sm" variant="outline" className="agentic-dash-button" onClick={onRefresh} disabled={!tenantId || isPeriodFilterLoading}>
        <RefreshCw className={isPeriodFilterLoading ? "animate-spin" : undefined} />Refresh
      </Button>
    </>,
    actionSlot
  );

  return (
    <div className="agentic-dash-overview">
      <div className="agentic-dash-scroll">
        {periodControls}
        {customRangeControls}
        {periodActions}

        <ActivityTimeline data={timeline} loading={loading} label={timelineLabel} />

        <div className={cn("agentic-kpi-grid", loading && "is-loading")}>
          {loading && (
            <div className="agentic-dash-overlay" role="status" aria-label="Loading summary">
              <Loader2 className="animate-spin" />
            </div>
          )}
          <StatCard icon={Zap} label="Total Events" value={overview?.totalEvents ?? 0} sub="across all endpoints" tone="violet" />
          <StatCard icon={Bot} label="AI Agents Detected" value={overview?.totalAgentsDetected ?? 0} sub={`${overview?.blockedAgents ?? 0} blocked`} tone="orange" />
          <StatCard icon={Monitor} label="Endpoints w/ AI" value={overview?.totalEndpointsWithAi ?? 0} sub="running AI agent processes" tone="sky" />
          <StatCard icon={Users} label="Active Users" value={overview?.activeUsers ?? 0} sub="interacting with AI agents" tone="teal" />
        </div>

        {error && (
          <div className="agentic-dash-alert" role="alert">
            <AlertTriangle aria-hidden="true" />
            <span>{error}</span>
            <Button size="sm" variant="outline" className="agentic-dash-button" onClick={loadDashboard}>Retry</Button>
          </div>
        )}

        <Card className="overview-card agentic-dash-section">
          <CardHeader className="overview-card-header">
            <div className="overview-card-heading">
              <span className="overview-heading-icon" aria-hidden="true"><Bot /></span>
              <div className="overview-heading-copy">
                <h2 className="overview-card-title">AI Agent Inventory</h2>
                <p className="overview-card-description">Click any agent to see which endpoints it is active on</p>
              </div>
            </div>
            <div className="agentic-dash-section-actions">
              {/**
               * 
               
              <div className="flex gap-1">
                {(["all", "APPROVED", "BLOCKED"] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => { setAgentFilter(f); setAgentPage(1); setSelectedAgentId(null); }}
                    className={`text-[11px] font-medium px-2.5 py-1 rounded-md border transition-colors ${agentFilter === f ? "bg-foreground text-background border-foreground" : "bg-background text-muted-foreground border-border hover-elevate"}`}
                  >
                    {f === "all" ? "All" : f === "APPROVED" ? "Approved" : "Blocked"}
                  </button>
                ))}
              </div>
              */}
              <div className="agentic-dash-search">
                <span className="agentic-dash-search-field">
                  <Search aria-hidden="true" />
                  <Input
                    placeholder="Search by agent name…"
                    value={agentNameInput}
                    onChange={e => handleAgentSearchInputChange(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleAgentSearch(); }}
                    disabled={agentsLoading}
                    aria-label="Search by agent name"
                  />
                </span>
                <Button size="icon" variant="outline" className="agentic-dash-icon-button" onClick={handleAgentSearch} disabled={agentsLoading} aria-label="Search agents">
                  {agentsLoading ? <Loader2 className="animate-spin" /> : <Search />}
                </Button>
              </div>
              {totalAgentPages > 1 && (
                <div className="agentic-dash-pager">
                  <Button size="icon" variant="outline" className="agentic-dash-icon-button" disabled={agentPage === 1} onClick={() => setAgentPage(p => p - 1)} aria-label="Previous agents page"><ChevronLeft /></Button>
                  <span>{agentPage} / {totalAgentPages}</span>
                  <Button size="icon" variant="outline" className="agentic-dash-icon-button" disabled={agentPage === totalAgentPages} onClick={() => setAgentPage(p => p + 1)} aria-label="Next agents page"><ChevronRight /></Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="overview-card-content">
            {agentsLoading ? (
              <LoadingState label="Loading agents…" />
            ) : pagedAgents.length === 0 ? (
              <EmptyState message="No agents detected yet" />
            ) : (
              <div className="agentic-agent-grid">
                {pagedAgents.map(agent => (
                  <AgentCard
                    key={agent.agentId}
                    agent={agent}
                    maxEvents={maxEvents}
                    onStatusChange={handleStatusChange}
                    onClick={() => setSelectedAgentId(selectedAgentId === agent.agentId ? null : agent.agentId)}
                    isSelected={selectedAgentId === agent.agentId}
                    statusUpdating={statusUpdating}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="overview-card agentic-dash-section">
          <CardHeader className="overview-card-header">
            <div className="overview-card-heading">
              <span className="overview-heading-icon" aria-hidden="true"><Users /></span>
              <div className="overview-heading-copy">
                <h2 className="overview-card-title">Endpoints &amp; Users</h2>
                <p className="overview-card-description">Ranked by event volume</p>
              </div>
            </div>
            <div className="agentic-dash-section-actions">
              <div className="agentic-dash-search">
                <span className="agentic-dash-search-field">
                  <Search aria-hidden="true" />
                  <Input
                    placeholder="Filter endpoints or users…"
                    value={tableSearchInput}
                    onChange={e => handleTableSearchInputChange(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleTableSearch(); }}
                    aria-label="Filter endpoints or users"
                  />
                </span>
                <Button size="icon" variant="outline" className="agentic-dash-icon-button" onClick={handleTableSearch} aria-label="Apply filter">
                  <Search />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overview-card-content">
            <div className={cn("agentic-rank-grid", loading && "is-loading")}>
              {loading && (
                <div className="agentic-dash-overlay" role="status" aria-label="Loading rankings">
                  <Loader2 className="animate-spin" />
                </div>
              )}
              <EndpointsTable items={topEndpoints} search={tableSearchQuery} loading={loading} />
              <UsersTable items={topUsers} search={tableSearchQuery} loading={loading} blockedAgentKeys={blockedAgentKeys} />
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedAgent && (
        <aside className="agentic-drilldown-wrapper" aria-label="Agent details">
          <AgentDrillDown
            agent={selectedAgent}
            tenantId={tenantId}
            onStatusChange={handleStatusChange}
            onClose={() => setSelectedAgentId(null)}
            statusUpdating={statusUpdating}
          />
        </aside>
      )}
    </div>
  );
}

function resolveActiveTenantId(): string {
  const storeTenant = useUserStore.getState().tenantName;
  const latestTenant = myLocalStorage.getItem("latestTenant");
  const storageTenant =
    typeof latestTenant === "string"
      ? latestTenant
      : latestTenant?.tenantName;
  const sessionTenant = useUserStore.getState().lastSession?.latestTenant;
  return (storeTenant || storageTenant || sessionTenant || "").trim();
}

export default function GlobalAgenticActivity() {
  const storeTenantName = useUserStore(state => state.tenantName);
  const [tenantId, setTenantId] = useState(resolveActiveTenantId);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const [overviewSummary, setOverviewSummary] = useState<AgenticActivityOverview | null>(null);
  const [periodSlot, setPeriodSlot] = useState<HTMLDivElement | null>(null);
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);
  const [extraSlot, setExtraSlot] = useState<HTMLDivElement | null>(null);

  const handleOverviewChange = useCallback((overview: AgenticActivityOverview | null) => {
    setOverviewSummary(overview);
  }, []);

  useEffect(() => {
    setTenantId(resolveActiveTenantId());
  }, [storeTenantName]);

  const handleRefresh = () => {
    setTenantId(resolveActiveTenantId());
    setRefreshKey(k => k + 1);
  };

  useEffect(() => {
    if (!tenantId) {
      setOverviewSummary(null);
      return;
    }
    getAgenticActivityOverview(tenantId, buildDashboardRangeParams("7"))
      .then(data => setOverviewSummary(data ?? null))
      .catch(() => setOverviewSummary(null));
  }, [tenantId, refreshKey]);


  return (
    <div className="endpoint-overview endpoint-data-sections agentic-dashboard" data-tab={activeTab}>
      <Card className="overview-card agentic-dashboard-header">
        <div className="overview-card-header">
          <div className="overview-card-heading">
            <span className="overview-heading-icon" aria-hidden="true"><Bot /></span>
            <div className="overview-heading-copy">
              <h1 className="overview-card-title agentic-dashboard-title">Agentic Activity</h1>
              <p className="overview-card-description">Enterprise-wide AI agent visibility &amp; governance</p>
            </div>
          </div>
          <span className="agentic-dashboard-live" data-live={Boolean(overviewSummary)}>
            <span className="agentic-dashboard-live-dot" aria-hidden="true" />
            {overviewSummary
              ? `${overviewSummary.totalEndpointsWithAi} endpoints · ${overviewSummary.totalEvents.toLocaleString()} events · live`
              : "Loading summary…"}
          </span>
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="agentic-dashboard-tabs-root">
        <div className="overview-card endpoint-section-toolbar agentic-dashboard-bar">
          <div ref={setPeriodSlot} className="agentic-dashboard-bar-start" />
          <TabsList unstyled aria-label="Agentic activity views" className="cea-tabs agentic-dashboard-tabs">
            <TabsTrigger unstyled value="overview" className={`cea-tab ${activeTab === "overview" ? "active" : ""}`}>
              <LayoutGrid className="shrink-0" />Global Overview
            </TabsTrigger>
            <TabsTrigger unstyled value="activity" className={`cea-tab ${activeTab === "activity" ? "active" : ""}`}>
              <Activity className="shrink-0" />Global Activity
            </TabsTrigger>
          </TabsList>
          <div ref={setActionSlot} className="agentic-dashboard-bar-end" />
          <div ref={setExtraSlot} className="agentic-dashboard-bar-extra" />
        </div>

        {activeTab === "overview" && (
          <div className="agentic-dashboard-panel">
            <OverviewTab
              tenantId={tenantId}
              refreshKey={refreshKey}
              onRefresh={handleRefresh}
              onOverviewChange={handleOverviewChange}
              periodSlot={periodSlot}
              actionSlot={actionSlot}
              extraSlot={extraSlot}
            />
          </div>
        )}
        {activeTab === "activity" && (
          <div className="agentic-dashboard-panel">
            <AgenticActivity global />
          </div>
        )}
      </Tabs>
    </div>
  );
}
